'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  LOGIN_CODE_RESEND_SECONDS,
  isPlausibleLoginCode,
  normalizeLoginCode,
  type RequestLoginCodeAction,
  type VerifyLoginCodeAction,
} from '@/lib/auth/login-code'
import { rememberEmail } from '@/lib/auth/remembered-email'

export type LoginCodeError =
  | 'invalid_email'
  | 'invalid_code'
  | 'rate_limited'
  | 'failed'
  | 'profile_error'

export interface UseLoginCodeOptions {
  request: RequestLoginCodeAction
  verify: VerifyLoginCodeAction
  next?: string | null
  initialEmail?: string
}

function isRedirect(error: unknown): boolean {
  return !!(error as { digest?: string } | null)?.digest?.startsWith('NEXT_REDIRECT')
}

/**
 * State of "Entrar con un código por mail", shared by the classic and the
 * Encuentro login screens so they cannot drift: two steps (address, code), a
 * resend cooldown, and results that are the same for every address.
 */
export function useLoginCode({ request, verify, next, initialEmail = '' }: UseLoginCodeOptions) {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCodeState] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<LoginCodeError | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback((seconds: number) => {
    if (timer.current) clearInterval(timer.current)
    setCooldown(seconds)
    timer.current = setInterval(() => {
      setCooldown((left) => {
        if (left <= 1) {
          if (timer.current) clearInterval(timer.current)
          return 0
        }
        return left - 1
      })
    }, 1000)
  }, [])

  useEffect(
    () => () => {
      if (timer.current) clearInterval(timer.current)
    },
    []
  )

  const send = useCallback(async (): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      const result = await request(email)
      if (result.ok) {
        setStep('code')
        startCooldown(result.cooldownSeconds || LOGIN_CODE_RESEND_SECONDS)
        return true
      }
      setError(result.reason)
      if (result.reason === 'rate_limited') startCooldown(result.cooldownSeconds)
      return false
    } catch {
      setError('failed')
      return false
    } finally {
      setBusy(false)
    }
  }, [email, request, startCooldown])

  const submitCode = useCallback(async () => {
    if (!isPlausibleLoginCode(code)) {
      setError('invalid_code')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await verify({ email, code, next })
      if (result && !result.ok) {
        setError(result.reason)
        if (result.reason === 'rate_limited') startCooldown(result.cooldownSeconds)
      }
      setBusy(false)
    } catch (e) {
      if (isRedirect(e)) {
        // Signed in: the page is leaving. Keep the spinner, remember the address.
        rememberEmail(email)
        return
      }
      setError('failed')
      setBusy(false)
    }
  }, [code, email, next, startCooldown, verify])

  const changeEmail = useCallback(() => {
    setStep('email')
    setCodeState('')
    setError(null)
  }, [])

  return {
    step,
    email,
    setEmail,
    code,
    setCode: (value: string) => setCodeState(normalizeLoginCode(value)),
    busy,
    error,
    cooldown,
    send,
    submitCode,
    changeEmail,
  }
}
